import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, throwError } from "rxjs";
import { environment } from "../../../../environment";
import { Contact, ContactListItem } from "../interfaces/contact.interface";
import {
  FacilityIntegration,
  FacilityIntegrationListItem,
} from "../interfaces/integration.interface";
import { ListResponse } from "../interfaces/list-response.interface";
import { Referral, ReferralListItem } from "../interfaces/referral.interface";
import { sanitizeParams } from "../sanitize-params";
import { ChargeCodeHistory, GenerateBilling, UploadChargeCodes } from "./billing.client";

@Injectable({ providedIn: "root" })
export class FacilityRelationsClient {
  endpoint = "facilitycontact";
  constructor(private httpClient: HttpClient) {}

  getContacts(filters: {
    facilityId: string;
  }): Observable<ListResponse<ContactListItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<ContactListItem>>(
      `${environment.endpoint}/facilitycontact/list/${filters.facilityId}`,
      { params }
    );
  }

  addContact(contact: Contact) {
    return this.httpClient.post(
      `${environment.endpoint}/facilitycontact/add`,
      contact
    );
  }

  editContact(contact: Contact) {
    return this.httpClient.put(
      `${environment.endpoint}/facilitycontact/edit`,
      contact
    );
  }

  deleteContact(id: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/facilitycontact/delete/${id}`
    );
  }

  getContact(id: string): Observable<Contact> {
    return this.httpClient.get<Contact>(
      `${environment.endpoint}/facilitycontact/get/${id}`
    );
  }

  enableContact(id: string) {
    return this.httpClient.put(
      `${environment.endpoint}/facilitycontact/enabled/${id}`,
      {}
    );
  }
  
  disableContact(id: string) {
    return this.httpClient.put(
      `${environment.endpoint}/facilitycontact/disabled/${id}`, {}
    );
  }

  getReferrals(filters: {
    facilityId: string;
  }): Observable<ListResponse<ReferralListItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<ReferralListItem>>(
      `${environment.endpoint}/facilityreferral/list/${filters.facilityId}`,
      { params }
    );
  }

  addReferral(contact: Referral) {
    return this.httpClient.post(
      `${environment.endpoint}/facilityreferral/add`,
      contact
    );
  }

  editReferral(contact: Referral) {
    return this.httpClient.put(
      `${environment.endpoint}/facilityreferral/edit`,
      contact
    );
  }

  deleteReferral(id: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/facilityreferral/delete/${id}`
    );
  }

  getReferral(id: string): Observable<Referral> {
    return this.httpClient.get<Referral>(
      `${environment.endpoint}/facilityreferral/get/${id}`
    );
  }

  enableReferral(id: string) {
    return this.httpClient.put(
      `${environment.endpoint}/facilityreferral/enabled/${id}`,
      null
    );
  }

  disableReferral(id: string) {
    return this.httpClient.put(
      `${environment.endpoint}/facilityreferral/disabled/${id}`,
      null
    );
  }

  addIntegration(integration: FacilityIntegration) {
    return this.httpClient.post(
      `${environment.endpoint}/facilityvendorintegration/add`,
      integration
    );
  }

  editIntegration(integration: FacilityIntegration) {
    return this.httpClient.put(
      `${environment.endpoint}/facilityvendorintegration/edit`,
      integration
    );
  }

  getIntegrations(filters: {
    facilityId: string;
  }): Observable<ListResponse<FacilityIntegrationListItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<FacilityIntegrationListItem>>(
      `${environment.endpoint}/facilityvendorintegration/list/${filters.facilityId}`,
      { params }
    );
  }

  deleteIntegration(id: number) {
    return this.httpClient.delete(
      `${environment.endpoint}/facilityvendorintegration/delete/${id}`
    );
  }

  addSpecialtyProgram(integration: FacilityIntegration) {
    return this.httpClient.post(
      `${environment.endpoint}/facilityvendorspecialtyprogram/add`,
      integration
    );
  }

  editSpecialtyProgram(integration: FacilityIntegration) {
    return this.httpClient.put(
      `${environment.endpoint}/facilityvendorspecialtyprogram/edit`,
      integration
    );
  }

  getSpecialties(filters: {
    facilityId: string;
  }): Observable<ListResponse<FacilityIntegrationListItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<FacilityIntegrationListItem>>(
      `${environment.endpoint}/facilityvendorspecialtyprogram/list/${filters.facilityId}`,
      { params }
    );
  }

  deleteSpecialtyProgram(id: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/facilityvendorspecialtyprogram/delete/${id}`
    );
  }

  generateBilling(generateBilling: GenerateBilling){
    return this.httpClient.post(
      `${environment.endpoint}/facility/get-billing-data/${generateBilling.facilityId}/${generateBilling.sendToVendor}/`,
      {
        startDate: generateBilling.startDate,
        endDate: generateBilling.endDate
      },
      { observe: 'response' ,
        responseType: 'blob' // Default to blob for successful responses
      }
    ).pipe(
        catchError((error) => {
            // In case of an error, change the responseType to 'text' for failure
            if (error.status >= 400) {
                return this.httpClient.post(
                    `${environment.endpoint}/facility/get-billing-data/${generateBilling.facilityId}/${generateBilling.sendToVendor}/`,
                    {},
                    {
                        observe: 'response',
                        responseType: 'text' // Set responseType to 'text' for failure
                    }
                );
            }
            return throwError(() => error); // Handle the error accordingly
        })
    );
  }

  uploadChargeCodes(uploadChargeCode: UploadChargeCodes){
    return this.httpClient.post(
      `${environment.endpoint}/facility/upload-facility-charge-codes/${uploadChargeCode.facilityId}/`,
       uploadChargeCode.file,
    );
  }

  downloadChargeCodes(facilityId: string, initialCodes?:boolean){
    const params = new HttpParams().set('isUpdated', initialCodes ? 'true' : 'false');
    return this.httpClient.post(
      `${environment.endpoint}/facility/get-facility-charge-codes/${facilityId}`,
      null,
      {
        params: params,
        observe: 'response',
        responseType: 'blob' // Default to blob for successful responses
      }
    ).pipe(
      catchError((error) => {
        // In case of an error, change the responseType to 'text' for failure
        if (error.status >= 400) {
          return this.httpClient.post(
            `${environment.endpoint}/facility/get-facility-charge-codes/${facilityId}/`,
            {},
            {
              observe: 'response',
              responseType: 'text' // Set responseType to 'text' for failure
            }
          );
        }
        return throwError(() => error); // Handle the error accordingly
      })
    );
  }
  
  getChargeCodeHistory(facilityId: string){
    return this.httpClient.post<ChargeCodeHistory>(
      `${environment.endpoint}/facility/get-facility-charge-code-update-history/${facilityId}/`,{}
    );
  }

}
